/**
 * sprint/sprintUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Pure utility functions for the Sprint View.
 */

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);

// ── Blocker phrases scanned in task comments ──────────────────────────────────
const BLOCKER_PHRASES = [
    'waiting for client',
    'need clarification',
    'blocked',
    'pending approval',
    'awaiting assets',
];

// ── KPI computation ───────────────────────────────────────────────────────────

/**
 * Computes KPIs for a given sprint window.
 *
 * @param {object[]} tasks          - tasks for the current sprint window
 * @param {string|null} nextSprintStart - ISO date string for next sprint start
 * @param {string|null} nextSprintEnd   - ISO date string for next sprint end
 * @param {object[]} allTasks       - full unfiltered task list (for next sprint count)
 */
export function computeSprintKpis(tasks, nextSprintStart, nextSprintEnd, allTasks = []) {
    let backlog = 0;
    let inProgress = 0;
    let review = 0;
    let completed = 0;
    let next = 0;

    tasks.forEach(t => {
        if (t.status === 'Backlog') backlog++;
        if (t.status === 'In Progress') inProgress++;
        if (t.status === 'Under Review') review++;
        if (t.status === 'Completed') completed++;
    });

    // Count tasks in the next sprint window
    if (nextSprintStart && nextSprintEnd) {
        const ns = dayjs(nextSprintStart);
        const ne = dayjs(nextSprintEnd);
        allTasks.forEach(t => {
            const due = t.dueDate ? dayjs(t.dueDate) : null;
            const start = t.startDate ? dayjs(t.startDate) : null;
            if ((due && due.isBetween(ns, ne, 'day', '[]')) ||
                (start && start.isBetween(ns, ne, 'day', '[]'))) {
                next++;
            }
        });
    }

    return { backlog, inProgress, review, completed, next };
}

export function computeCompletionRate(tasks) {
    if (!tasks || tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.status === 'Completed').length;
    return Math.round((completed / tasks.length) * 100);
}

// ── Filter tasks to a sprint window ──────────────────────────────────────────

/**
 * Returns tasks whose dueDate or startDate falls within [sprintStart, sprintEnd].
 * If either bound is null, returns all tasks unfiltered.
 */
export function filterTasksToSprint(tasks, sprintStart, sprintEnd) {
    if (!sprintStart || !sprintEnd) return tasks;
    const s = dayjs(sprintStart).startOf('day');
    const e = dayjs(sprintEnd).endOf('day');
    return tasks.filter(t => {
        const due = t.dueDate ? dayjs(t.dueDate) : null;
        const start = t.startDate ? dayjs(t.startDate) : null;
        return (due && due.isBetween(s, e, 'day', '[]')) ||
            (start && start.isBetween(s, e, 'day', '[]'));
    });
}

// ── AI Insights ───────────────────────────────────────────────────────────────

export function deriveSprintInsights(tasks) {
    if (!tasks || tasks.length === 0) return [];

    const insights = [];
    const now = dayjs();

    // ── 1. Workload alert — anyone with >4 active tasks ───────────────────────
    const assigneeMap = {};
    tasks.forEach(t => {
        if (['Completed', 'Cancelled'].includes(t.status)) return;
        const name = [t.assignee?.name, t.assignee?.surname].filter(Boolean).join(' ') || 'Unassigned';
        if (!assigneeMap[name]) assigneeMap[name] = [];
        assigneeMap[name].push(t);
    });

    let maxLoad = 0, maxPerson = null, maxPersonTasks = [];
    Object.entries(assigneeMap).forEach(([name, userTasks]) => {
        if (name !== 'Unassigned' && userTasks.length > maxLoad) {
            maxLoad = userTasks.length;
            maxPerson = name;
            maxPersonTasks = userTasks;
        }
    });

    if (maxLoad > 4) {
        insights.push({
            type: 'error',
            icon: 'warning',
            title: 'Workload Alert',
            message: `${maxPerson} is over-allocated with ${maxLoad} active tasks. Consider redistributing lower priority work.`,
            relatedTasks: maxPersonTasks,
        });
    }

    // ── 2. Review bottleneck — 3+ tasks stuck in Under Review ────────────────
    const reviewTasks = tasks.filter(t => t.status === 'Under Review');
    if (reviewTasks.length >= 3) {
        insights.push({
            type: 'warning',
            icon: 'schedule',
            title: 'Review Bottleneck Detected',
            message: `${reviewTasks.length} tasks are sitting "Under Review", causing a pipeline stall.`,
            relatedTasks: reviewTasks,
        });
    }

    // ── 3. Deadline prediction — due within 2 days and not near done ─────────
    const atRiskTasks = tasks.filter(t => {
        if (['Completed', 'Cancelled', 'On Hold'].includes(t.status)) return false;
        if (!t.dueDate) return false;
        const due = dayjs(t.dueDate);
        return (due.isBefore(now) || due.diff(now, 'day') <= 2);
    });
    if (atRiskTasks.length > 0) {
        insights.push({
            type: 'info',
            icon: 'event_upcoming',
            title: 'Deadline Prediction',
            message: `${atRiskTasks.length} task${atRiskTasks.length > 1 ? 's are' : ' is'} unlikely to be completed on time based on current progress.`,
            relatedTasks: atRiskTasks,
        });
    }

    // ── 4. Status-based blocker — On Hold tasks ───────────────────────────────
    const onHoldTasks = tasks.filter(t => t.status === 'On Hold');
    if (onHoldTasks.length > 0) {
        insights.push({
            type: 'warning',
            icon: 'block',
            title: 'Blocker Detection',
            message: `${onHoldTasks.length} task${onHoldTasks.length > 1 ? 's' : ''} ${onHoldTasks.length > 1 ? 'are' : 'is'} currently "On Hold". Unblocking these should be the priority.`,
            relatedTasks: onHoldTasks,
        });
    }

    // ── 5. Comment-based blocker detection ───────────────────────────────────
    const commentBlockedTasks = tasks.filter(t => {
        if (['Completed', 'Cancelled'].includes(t.status)) return false;
        if (!Array.isArray(t.comments) || t.comments.length === 0) return false;
        return t.comments.some(c => {
            const text = (c.text || '').toLowerCase();
            return BLOCKER_PHRASES.some(phrase => text.includes(phrase));
        });
    });
    if (commentBlockedTasks.length > 0) {
        // Collect the matched phrases for the message
        const matchedPhrases = new Set();
        commentBlockedTasks.forEach(t => {
            (t.comments || []).forEach(c => {
                const text = (c.text || '').toLowerCase();
                BLOCKER_PHRASES.forEach(phrase => {
                    if (text.includes(phrase)) matchedPhrases.add(`"${phrase}"`);
                });
            });
        });
        insights.push({
            type: 'error',
            icon: 'block',
            title: 'Comment-Based Blockers Detected',
            message: `${commentBlockedTasks.length} task${commentBlockedTasks.length > 1 ? 's' : ''} contain blocker phrases in comments (${[...matchedPhrases].slice(0, 3).join(', ')}). Immediate action needed.`,
            relatedTasks: commentBlockedTasks,
        });
    }

    // ── 6. Sprint optimization — fallback all-clear ───────────────────────────
    if (insights.length === 0) {
        insights.push({
            type: 'info',
            icon: 'check_circle',
            title: 'Sprint Optimization',
            message: 'Sprint is progressing efficiently. Workload is balanced and no immediate bottlenecks detected.',
            relatedTasks: [],
        });
    }

    return insights;
}
